import React from 'react';
import { Vehicule } from '../../../../shared/types/game';
import { VehiculeConfig } from '../../../../shared/types/config';

/** Carte véhicule (vue compacte ou détaillée) extraite de BuildingVehiculesTab.tsx. */

function getStatutColors(statut: string): string {
  switch (statut) {
    case 'disponible':
      return 'bg-green-500/20 text-green-400';
    case 'en_mission':
      return 'bg-yellow-500/20 text-yellow-400';
    case 'en_maintenance':
      return 'bg-orange-500/20 text-orange-400';
    case 'panne':
      return 'bg-red-500/20 text-red-400';
    default:
      return 'bg-gray-500/20 text-zinc-400';
  }
}

function getStatutLabel(statut: string): string {
  switch (statut) {
    case 'disponible':
      return 'Disponible';
    case 'en_mission':
      return 'En mission';
    case 'en_maintenance':
      return 'En maintenance';
    case 'panne':
      return 'Panne';
    default:
      return statut;
  }
}

interface VehiculeCardProps {
  v: Vehicule;
  compact: boolean;
  onOpenDetails: (v: Vehicule) => void;
  nbInterventions: number;
  config: VehiculeConfig | null;
}

const VehiculeCard = React.memo<VehiculeCardProps>(
  ({ v, compact, onOpenDetails, nbInterventions, config }) => {
    if (compact) {
      return (
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 hover:border-sky-500 transition-colors aspect-square flex flex-col items-center justify-center relative group">
          <div className="absolute top-2 right-2">
            <span className={`px-1.5 py-0.5 rounded-full text-xs ${getStatutColors(v.statut)}`}>
              {v.statut === 'disponible'
                ? '✓'
                : v.statut === 'en_mission'
                  ? '→'
                  : v.statut === 'en_maintenance'
                    ? '⚙'
                    : '✗'}
            </span>
          </div>

          <h4 className="text-sm font-semibold text-white text-center truncate w-full px-2 mb-1">
            {v.nom}
          </h4>

          <span className="text-xs text-sky-400 text-center mb-1">{v.type}</span>

          <div className="mb-2">
            <span className="px-2 py-1 bg-sky-500/20 text-sky-400 rounded text-xs">
              {nbInterventions} intervention{nbInterventions > 1 ? 's' : ''}
            </span>
          </div>

          <span className="text-xs text-zinc-400 text-center mb-2">
            {v.kilometrage.toLocaleString()} km
          </span>

          {config && (
            <div className="mt-auto pt-2 border-t border-zinc-700 w-full space-y-1">
              {(config.capable_escorte_medicale || config.capable_escorte_fdo) && (
                <div className="text-center">
                  <span className="text-xs text-zinc-400">
                    {config.capable_escorte_medicale && 'Esc. Méd. '}
                    {config.capable_escorte_fdo && 'Esc. FDO'}
                  </span>
                </div>
              )}
              {(config.capacite_victimes_ur ||
                config.capacite_victimes_ua ||
                config.capacite_victimes_impliquees ||
                config.capacite_hostiles ||
                config.capacite_vehicules_accidentes) && (
                <div className="text-center text-xs text-zinc-400">
                  {config.capacite_victimes_ur && `UR: ${config.capacite_victimes_ur} `}
                  {config.capacite_victimes_ua && `UA: ${config.capacite_victimes_ua} `}
                  {config.capacite_victimes_impliquees &&
                    `Impl: ${config.capacite_victimes_impliquees} `}
                  {config.capacite_hostiles && `Host: ${config.capacite_hostiles} `}
                  {config.capacite_vehicules_accidentes &&
                    `Véh: ${config.capacite_vehicules_accidentes}`}
                </div>
              )}
            </div>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails(v);
            }}
            className="absolute top-2 left-2 text-zinc-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
            title="Voir les détails"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
              />
            </svg>
          </button>
        </div>
      );
    }

    // Vue détaillée
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 hover:border-sky-500 transition-colors">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-white">{v.nom}</h4>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="px-2 py-1 bg-sky-500/20 text-sky-400 rounded text-xs">
                {v.type}
              </span>
              <span className={`px-2 py-1 rounded text-xs ${getStatutColors(v.statut)}`}>
                {getStatutLabel(v.statut)}
              </span>
              <span className="px-2 py-1 bg-sky-500/20 text-sky-400 rounded text-xs">
                {nbInterventions} intervention{nbInterventions > 1 ? 's' : ''}
              </span>
              {v.capacite && (
                <span className="text-xs text-zinc-400">Capacité: {v.capacite}</span>
              )}
              <span className="text-xs text-zinc-400">{v.kilometrage.toLocaleString()} km</span>
            </div>
            {config && (
              <div className="mt-3 space-y-2">
                {(config.capable_escorte_medicale || config.capable_escorte_fdo) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {config.capable_escorte_medicale && (
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                        Escorte Médicale
                      </span>
                    )}
                    {config.capable_escorte_fdo && (
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
                        Escorte FDO
                      </span>
                    )}
                  </div>
                )}
                {(config.capacite_victimes_ur ||
                  config.capacite_victimes_ua ||
                  config.capacite_victimes_impliquees ||
                  config.capacite_hostiles ||
                  config.capacite_vehicules_accidentes) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {config.capacite_victimes_ur !== undefined && config.capacite_victimes_ur > 0 && (
                      <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                        UR: {config.capacite_victimes_ur}
                      </span>
                    )}
                    {config.capacite_victimes_ua !== undefined && config.capacite_victimes_ua > 0 && (
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
                        UA: {config.capacite_victimes_ua}
                      </span>
                    )}
                    {config.capacite_victimes_impliquees !== undefined &&
                      config.capacite_victimes_impliquees > 0 && (
                        <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-xs">
                          Impliqués: {config.capacite_victimes_impliquees}
                        </span>
                      )}
                    {config.capacite_hostiles !== undefined && config.capacite_hostiles > 0 && (
                      <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
                        Hostiles: {config.capacite_hostiles}
                      </span>
                    )}
                    {config.capacite_vehicules_accidentes !== undefined &&
                      config.capacite_vehicules_accidentes > 0 && (
                        <span className="px-2 py-1 bg-gray-500/20 text-zinc-400 rounded text-xs">
                          Véhicules: {config.capacite_vehicules_accidentes}
                        </span>
                      )}
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails(v);
            }}
            className="ml-4 text-zinc-400 hover:text-white transition-colors p-2 hover:bg-zinc-700 rounded-lg"
            title="Voir les détails"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }
);

VehiculeCard.displayName = 'VehiculeCard';

export default VehiculeCard;

